// مشترک صفحات DOC (AP-DOC-01): شناسه صفحات، کلیدهای Query، رنگ وضعیت، دریافت Blob با توکن و نشان «نمونه».
import type { DocConsentTemplateStatus } from '@phoenix/api-client';
import { StatusChip, type StatusTone } from '@phoenix/ui';

export const SCR_STAFF_600 = 'SCR-STAFF-600';
export const SCR_STAFF_601 = 'SCR-STAFF-601';
export const SCR_STAFF_602 = 'SCR-STAFF-602';
export const SCR_STAFF_603 = 'SCR-STAFF-603';
export const SCR_STAFF_604 = 'SCR-STAFF-604';
export const SCR_STAFF_605 = 'SCR-STAFF-605';
export const SCR_STAFF_607 = 'SCR-STAFF-607';
export const SCR_STAFF_608 = 'SCR-STAFF-608';
/** فیلدهای مشترک (US-DOC-152). */
export const SCR_STAFF_616 = 'SCR-STAFF-616';
/** بارگذاری موبایل با QR (US-DOC-154). */
export const SCR_STAFF_617 = 'SCR-STAFF-617';
export const SCR_TABLET_001 = 'SCR-TABLET-001';
export const SCR_PUBLIC_010 = 'SCR-PUBLIC-010';
export const SCR_PUBLIC_011 = 'SCR-PUBLIC-011';
export const SCR_PUBLIC_012 = 'SCR-PUBLIC-012';
export const COMP_DOC_VIEWER = 'COMP-DOC-VIEWER';

export const DOC_API = '/api/v1/documents';

export const docKeys = {
  all: (tenantId?: string) => ['documents', tenantId] as const,
  documents: (tenantId: string | undefined, patientId: string, filter: string) =>
    ['documents', tenantId, 'documents', patientId, filter] as const,
  document: (tenantId: string | undefined, id: string) => ['documents', tenantId, 'document', id] as const,
  shares: (tenantId: string | undefined, id: string) => ['documents', tenantId, 'shares', id] as const,
  types: (tenantId?: string) => ['documents', tenantId, 'types'] as const,
  // AP-INT-05: ایستگاه‌های دارای اسکنر یا پد امضا.
  captureStations: (tenantId?: string) => ['documents', tenantId, 'capture-stations'] as const,
  settings: (tenantId?: string) => ['documents', tenantId, 'settings'] as const,
  formTemplates: (tenantId?: string) => ['documents', tenantId, 'form-templates'] as const,
  publishedForms: (tenantId?: string) => ['documents', tenantId, 'published-forms'] as const,
  formLibrary: (tenantId?: string) => ['documents', tenantId, 'form-library'] as const,
  submissions: (tenantId: string | undefined, patientId: string) => ['documents', tenantId, 'submissions', patientId] as const,
  submission: (tenantId: string | undefined, id: string) => ['documents', tenantId, 'submission', id] as const,
  review: (tenantId: string | undefined, id: string) => ['documents', tenantId, 'review', id] as const,
  requests: (tenantId: string | undefined, patientId: string) => ['documents', tenantId, 'form-requests', patientId] as const,
  consentTemplates: (tenantId?: string) => ['documents', tenantId, 'consent-templates'] as const,
  publishedConsents: (tenantId?: string) => ['documents', tenantId, 'published-consents'] as const,
  consentLibrary: (tenantId?: string) => ['documents', tenantId, 'consent-library'] as const,
  consents: (tenantId: string | undefined, patientId: string) => ['documents', tenantId, 'consents', patientId] as const,
  consent: (tenantId: string | undefined, id: string) => ['documents', tenantId, 'consent', id] as const,
  signatureRequests: (tenantId: string | undefined, patientId: string) =>
    ['documents', tenantId, 'signature-requests', patientId] as const,
  printTemplates: (tenantId?: string) => ['documents', tenantId, 'print-templates'] as const,
  tablet: (tenantId: string | undefined, id: string) => ['documents', tenantId, 'tablet', id] as const,
  sharedFields: (tenantId?: string) => ['documents', tenantId, 'shared-fields'] as const,

  // AP-DOC-03 (SCR-STAFF-607).
  retentionClasses: (tenantId?: string) => ['documents', tenantId, 'retention-classes'] as const,
  legalHolds: (tenantId: string | undefined, onlyActive: boolean) => ['documents', tenantId, 'legal-holds', onlyActive] as const,
  destructionRuns: (tenantId?: string) => ['documents', tenantId, 'destruction-runs'] as const,
  destructionRun: (tenantId: string | undefined, id: string) => ['documents', tenantId, 'destruction-run', id] as const,
};

/** رنگ وضعیت رضایت در لحظه (AC-DOC-044.1). */
export function consentTone(state: string): StatusTone {
  switch (state) {
    case 'Satisfied':
      return 'done';
    case 'RenewalRequired':
      return 'pending';
    case 'Expired':
    case 'Revoked':
      return 'error';
    default:
      return 'inactive';
  }
}

export function ConsentStateChip({ state, testId }: { state: DocConsentTemplateStatus['state'] | string; testId?: string }) {
  return (
    <span data-testid={testId} data-state={state}>
      <StatusChip tone={consentTone(state)} labelKey={`doc:consent_state.${state}`} />
    </span>
  );
}

/** رنگ وضعیت فرم. */
export function submissionTone(status: string): StatusTone {
  switch (status) {
    case 'Completed':
      return 'done';
    case 'Submitted':
      return 'confirmed';
    case 'Draft':
      return 'pending';
    case 'Returned':
      return 'error';
    default:
      return 'inactive';
  }
}

export function SubmissionStatusChip({ status, testId }: { status: string; testId?: string }) {
  return (
    <span data-testid={testId} data-status={status}>
      <StatusChip tone={submissionTone(status)} labelKey={`doc:submission_status.${status}`} />
    </span>
  );
}

/** عدد از قرارداد (int ممکن است رشته باشد). */
export function num(value: number | string | null | undefined): number {
  if (value === null || value === undefined) {
    return 0;
  }
  return typeof value === 'number' ? value : Number(value);
}

/** اندازه فایل خوانا. */
export function fileSize(bytes: number | string): string {
  const size = num(bytes);
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

/** ذخیره Blob با نام فایل. */
export function saveDocBlob(blob: Blob, fileName: string) {
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
